// G4 + G6 (batch 2): "Create follow-up" from a task or event, at completion (result "Create follow-up",
// code CUSTOM) or at any time from the record. A task gets a date and an optional time and never an end;
// an event gets a start and an end. Who it is for defaults to you; the reminder is optional; the notes are
// carried across. The server checks the same rules with the same words (FollowUpRequest.kt).
import { kindOfRecord, kindOfType } from '@/mbp/activityKind'

const pad = n => String(n).padStart(2, '0')
export const fmtDate = d => (d ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` : null)
export const fmtTime = d => (d ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : null)

// The source's notes and result notes, one after the other.
export function carriedNotes (source) {
  return [source && source.notes, source && source.resultNotes]
    .map(n => (n || '').trim())
    .filter(Boolean)
    .join('\n')
}

// A new follow-up form for [source], for [me]. It starts as the same kind as the source.
export function newFollowUp (source, me) {
  return {
    kind: kindOfRecord(source) || 'TASK',
    type: null,
    subject: source && source.subject ? `Follow-up: ${source.subject}` : '',
    user: me || null,
    date: null,
    time: null,
    endDate: null,
    endTime: null,
    reminder: false,
    reminderDate: null,
    reminderTime: null,
    notes: carriedNotes(source)
  }
}

const at = (date, time) => (date ? new Date(date.getFullYear(), date.getMonth(), date.getDate(),
  time ? time.getHours() : 0, time ? time.getMinutes() : 0) : null)

// What is wrong with the form, in plain words; null when it can be saved.
export function followUpProblem (form) {
  if (!form.type) return 'Choose the type of the follow-up.'
  if (!form.subject || !form.subject.trim()) return 'Give the follow-up a subject.'
  const isTask = kindOfType(form.type) === 'TASK'
  if (!form.date) return isTask ? 'Give the task a date.' : 'Give the event a start date.'
  if (!isTask) {
    if (!form.time) return 'Give the event a start time.'
    if (!form.endDate || !form.endTime) return 'Give the event an end date and time.'
    if (at(form.endDate, form.endTime) <= at(form.date, form.time)) return 'The event must end after it starts.'
  }
  if (form.reminder && (!form.reminderDate || !form.reminderTime)) return 'Give the reminder a date and time, or switch it off.'
  return null
}

// The body the server reads (POST v1/activity/{id}/followUp, or `followUp` on a completion).
export function followUpBody (form) {
  const isTask = kindOfType(form.type) === 'TASK'
  return {
    activityTypeId: form.type ? form.type.id : null,
    subject: (form.subject || '').trim(),
    userId: form.user ? form.user.id : null,
    startDate: fmtDate(form.date),
    startTime: fmtTime(form.time),
    endDate: isTask ? null : fmtDate(form.endDate),
    endTime: isTask ? null : fmtTime(form.endTime),
    reminder: !!form.reminder,
    reminderDate: form.reminder ? fmtDate(form.reminderDate) : null,
    reminderTime: form.reminder ? fmtTime(form.reminderTime) : null,
    notes: (form.notes || '').trim() || null
  }
}

// The status update a completion sends: the result, its notes, and the follow-up when one was asked for.
// One place for the five screens that complete a task or event (they each built this by hand).
export function completionBody (newStatusId, completed) {
  const body = {
    newStatusId,
    resultId: completed.activityResult ? completed.activityResult.id : null,
    resultNotes: completed.notes
  }
  if (completed.followUp) body.followUp = completed.followUp
  return body
}
